import pushnotificationService from '../../../services/pushnotificationService';
import filterService from '../../../services/filterService';

import {
    setPushNotificationLoading,
    setPushNotificationError,
    setPushNotificationList,
    setPushNotificationCurrent,
    setPushNotificationPagination,
    setPushNotificationSort,
    setPushNotificationFilters,
    setPushNotificationDefaultFilter,
    setPushNotificationSettings,
    clearPushNotificationCurrent,
    clearPushNotificationError,
    setPushNotificationFilterLoading,
    changePushNotificationFilterCondition,
    addPushNotificationFilterCondition,
    updatePushNotificationFilterConditionRule,
    updatePushNotificationFilterConditionValue,
    removePushNotificationFilterCondition,
    resetPushNotificationFiltersAndPagination
} from './pushnotificationSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().pushnotification.isSettingsInitialized) {
            dispatch(setPushNotificationLoading(true));
            const response = await pushnotificationService.getSettings();

            // Set the settings
            dispatch(setPushNotificationSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setPushNotificationDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setPushNotificationError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setPushNotificationLoading(false));
    }
};

export const fetchPushnotifications = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().pushnotification;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setPushNotificationLoading(true));
        const response = await pushnotificationService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setPushNotificationList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchPushnotifications:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setPushNotificationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPushNotificationLoading(false));
    }
};

export const fetchPushNotification = (id) => async (dispatch) => {
    try {
        dispatch(setPushNotificationLoading(true));
        const response = await pushnotificationService.getOne(id);
        dispatch(setPushNotificationCurrent(response.data));
    } catch (error) {
        dispatch(setPushNotificationError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setPushNotificationLoading(false));
    }
};

export const createPushNotification = (data) => async (dispatch) => {
    try {
        const response = await pushnotificationService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updatePushNotification = (id, data) => async (dispatch) => {
    try {
        const response = await pushnotificationService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deletePushNotification = (id) => async (dispatch) => {
    try {
        dispatch(setPushNotificationLoading(true));
        const response = await pushnotificationService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setPushNotificationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPushNotificationLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setPushNotificationPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setPushNotificationSort(sort));
};

export const searchPushnotifications = (query) => (dispatch, getState) => {
    const currentFilters = getState().pushnotification.filters;
    dispatch(setPushNotificationFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setPushNotificationDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changePushNotificationFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addPushNotificationFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updatePushNotificationFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updatePushNotificationFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removePushNotificationFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setPushNotificationFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().pushnotification.settings;
            dispatch(setPushNotificationSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setPushNotificationFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setPushNotificationFilterLoading(false));
        dispatch(setPushNotificationError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetPushNotificationFiltersAndPagination());
};